import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FaShieldAlt, FaSearch, FaTimes, FaExclamationTriangle,
  FaTrash, FaPlus, FaSave, FaSync,
} from "react-icons/fa";
import { schaedenApi } from "../../api/versicherungsschaeden";

/* ─── Utils ────────────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return "";
  // Immer zweistellig (07.07.2026), wie überall sonst in der Anwendung
  return new Date(d).toLocaleDateString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
};
const fmtJahr = (d) => (d ? String(d).slice(0, 4) : "");
const fmtEUR = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
};

/* Bekannte Status-Werte aus der Quell-Excel. Frontend bietet diese als
   Dropdown, erlaubt aber auch Freitext. */
const STATUS_SCHADEN = [
  "Angelegt", "Offen", "Gemeldet", "Nicht gemeldet", "Eingereicht", "Nicht eingereicht",
  "Nicht versichert", "Bezahlt", "Erledigt", "Abgeschlossen",
  "Geschlossen", "Geschlossen ohne Leistung",
];
/* Prozess-Kette wie bei den Vorgängen: Klick setzt den Status direkt. */
const STATUS_KETTE = ["Angelegt", "Gemeldet", "Eingereicht", "Bezahlt", "Erledigt"];
/* Nur echte Objekte (005, 087, …) — keine Einheiten (005-01), Zähler oder KFZ-Kürzel. */
const nurBasisObjekte = (objekte) =>
  objekte.filter((o) => /^[0-9A-Za-z]{3}$/.test(o.kuerzel || ""));
const dateiName = (pfad) => (pfad ? String(pfad).split("/").pop() : "");
const STATUS_DOKUMENT = ["", "liegt vor", "zu erstellen", "eingereicht", "rueckgemeldet"];
const RICHTUNG = ["eingereicht", "eingehend"];

function statusClass(s) {
  const x = (s || "").toLowerCase();
  if (!x) return "me-badge-none";
  if (x.includes("offen") || x.includes("nicht gemeldet")) return "me-badge-warn";
  if (x.includes("eingereicht") || x.includes("gemeldet")) return "me-badge-info";
  if (x.includes("bezahlt")) return "me-badge-ok";
  if (x.includes("erledigt") || x.includes("abgeschlossen") || x.includes("geschlossen"))
    return "me-badge-done";
  if (x.includes("nicht versichert")) return "me-badge-muted";
  return "me-badge-none";
}

/* Excel-artiger Spaltenfilter: kleines Select mit den tatsächlich vorkommenden Werten. */
function FilterSelect({ wert, werte, ph, onWert }) {
  return (
    <select className="me-select me-select-sm" value={wert} style={{ width: "100%", fontWeight: 400 }}
            onChange={(e) => onWert(e.target.value)}>
      <option value="">{ph}: alle</option>
      {werte.map((w) => <option key={w} value={w}>{w}</option>)}
    </select>
  );
}

/* Welle 89: das Versicherungs-Dropdown ist dedupliziert (eine Option je Police, `ids` =
   alle DB-Zeilen der Gruppe). Ein Schaden kann aber auf eine Nicht-Gruppen-ID zeigen —
   dieser Helfer findet die passende Option. */
function versGruppe(versicherungen, vid) {
  if (!vid) return null;
  const sVid = String(vid);
  return versicherungen.find(
    (v) => String(v.id) === sVid || String(v.ids || "").split(",").includes(sVid)
  ) || null;
}

function versOptionLabel(v) {
  const ges = v.gesellschaft_kuerzel || v.gesellschaft || "?";
  const obj = v.objekte || v.objekt_kuerzel;
  return `${ges} · ${v.versicherungsnummer}${obj ? ` (${obj})` : ""}`;
}

/* ─── Sub-Komponente: Drawer ───────────────────────────────────────────── */
function SchadenDrawer({ schadenId, versicherungen, objekte, onClose, onChanged }) {
  const [allePolicen, setAllePolicen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadRichtung, setUploadRichtung] = useState("eingereicht");
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const r = await schaedenApi.get(schadenId);
      setData(r);
      setEdits({});
    } catch (e) {
      setError(e.message);
    }
  }, [schadenId]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (k, v) => setEdits((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!Object.keys(edits).length) { onClose(); return; }
    setSaving(true);
    try {
      await schaedenApi.update(schadenId, edits);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const removeSchaden = async () => {
    if (!window.confirm("Diesen Schadensfall wirklich löschen? Alle Dokumente werden mit gelöscht.")) return;
    try {
      await schaedenApi.remove(schadenId);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(e.message);
    }
  };

  const addDoc = async () => {
    try {
      await schaedenApi.addDokument(schadenId, { richtung: "eingereicht" });
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const updateDoc = async (dokId, patch) => {
    try {
      await schaedenApi.updateDokument(dokId, patch);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeDoc = async (dokId) => {
    if (!window.confirm("Dokument löschen?")) return;
    try {
      await schaedenApi.removeDokument(dokId);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!data) {
    return (
      <div className="me-drawer-overlay" onClick={onClose}>
        <div className="me-drawer" onClick={(e) => e.stopPropagation()}>
          <p style={{ padding: 20 }}>Lade…</p>
        </div>
      </div>
    );
  }

  const val = (k) => (edits[k] !== undefined ? edits[k] : data[k] ?? "");

  return (
    <div className="me-drawer-overlay" onClick={onClose}>
      <div className="me-drawer me-drawer-wide" onClick={(e) => e.stopPropagation()}>
        <div className="me-drawer-header">
          <h3>
            <FaShieldAlt /> Schaden #{data.id}
            {data.schaden_nr && <span style={{ color: "#94a3b8", marginLeft: 8 }}>· {data.schaden_nr}</span>}
          </h3>
          <button className="me-btn me-btn-icon" onClick={onClose}><FaTimes /></button>
        </div>

        {error && <div className="me-status error">❌ {error}</div>}
        {!data.versicherung_id && (
          <div className="me-warning-section" style={{ marginBottom: 12 }}>
            <div className="me-warning-item">
              <FaExclamationTriangle />
              <span>Keine Police verknüpft — Dropdown unten zum Verknüpfen nutzen.</span>
            </div>
          </div>
        )}

        <div className="me-drawer-body">
          {/* ── Prozess-Kette (Welle 89.1): Klick = Status sofort setzen ── */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {STATUS_KETTE.map((st) => (
              <button key={st}
                      className={`vg-chip ${(data.status || edits.status) === st ? "aktiv" : ""}`}
                      onClick={async () => {
                        try { await schaedenApi.update(schadenId, { status: st }); await load(); onChanged?.(); }
                        catch (e) { setError(e.message); }
                      }}>{st}</button>
            ))}
          </div>
          {/* ── Stammdaten ───────────────────────────────────────────── */}
          {(() => {
            /* Welle 89: erst Objekt, dann nur dessen Policen — die aktuell verknüpfte
               Police bleibt immer wählbar, sonst würde der gespeicherte Wert unsichtbar. */
            const objWert = val("objekt_kuerzel");
            const aktuelleGruppe = versGruppe(versicherungen, val("versicherung_id"));
            const gefiltert = versicherungen.filter((v) => {
              if (allePolicen || !objWert) return true;
              if (aktuelleGruppe && v.id === aktuelleGruppe.id) return true;
              return String(v.objekte || "").split(",").map((x) => x.trim()).includes(objWert);
            });
            const selectWert = aktuelleGruppe ? aktuelleGruppe.id : (val("versicherung_id") || "");
            return (
          <div className="vs-form-grid">
            <label>Objekt
              <select value={objWert || ""} onChange={(e) => setEdit("objekt_kuerzel", e.target.value)}>
                <option value="">—</option>
                {nurBasisObjekte(objekte || []).map((o) => (
                  <option key={o.kuerzel} value={o.kuerzel}>{o.kuerzel}{o.bezeichnung ? ` – ${o.bezeichnung}` : ""}</option>
                ))}
                {objWert && !nurBasisObjekte(objekte || []).some((o) => o.kuerzel === objWert) && (
                  <option value={objWert}>{objWert}</option>
                )}
              </select>
            </label>
            <label>Versicherung
              <select
                value={selectWert}
                onChange={(e) => setEdit("versicherung_id", e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— ohne Police —</option>
                {gefiltert.map((v) => (
                  <option key={v.id} value={v.id}>{versOptionLabel(v)}</option>
                ))}
                {aktuelleGruppe === null && val("versicherung_id") ? (
                  <option value={val("versicherung_id")}>Police #{val("versicherung_id")}</option>
                ) : null}
              </select>
              {objWert && !allePolicen && (
                <button type="button" className="me-btn me-btn-sm me-btn-secondary"
                        style={{ marginTop: 4 }} onClick={() => setAllePolicen(true)}>
                  alle Policen anzeigen
                </button>
              )}
            </label>
            <label>Schadenstag
              <input type="date" value={val("schadenstag") ? String(val("schadenstag")).slice(0, 10) : ""}
                     onChange={(e) => setEdit("schadenstag", e.target.value || null)} />
            </label>
            <label className="vs-checkbox-label">
              <input type="checkbox"
                     checked={!!val("jahr_geschaetzt")}
                     onChange={(e) => setEdit("jahr_geschaetzt", e.target.checked)} />
              <span>nur Jahr bekannt</span>
            </label>
            <label>Art
              <input value={val("art")} onChange={(e) => setEdit("art", e.target.value)} placeholder="LW, Brand, Sturm, Glas…" />
            </label>
            <label>Schaden-Nr
              <input value={val("schaden_nr")} onChange={(e) => setEdit("schaden_nr", e.target.value)} />
            </label>
            <label>Höhe geschätzt €
              <input type="number" step="0.01" value={val("hoehe_geschaetzt") || ""}
                     onChange={(e) => setEdit("hoehe_geschaetzt", e.target.value ? Number(e.target.value) : null)} />
            </label>
            <label>Höhe real €
              <input type="number" step="0.01" value={val("hoehe_real") || ""}
                     onChange={(e) => setEdit("hoehe_real", e.target.value ? Number(e.target.value) : null)} />
            </label>
            <label>Polizeiliches AZ
              <input value={val("polizeiliches_az")} onChange={(e) => setEdit("polizeiliches_az", e.target.value)} />
            </label>
            <label>Status
              <input list="vs-status-list" value={val("status")} onChange={(e) => setEdit("status", e.target.value)} />
              <datalist id="vs-status-list">
                {STATUS_SCHADEN.map((s) => <option key={s} value={s} />)}
              </datalist>
            </label>
            <label style={{ gridColumn: "1 / -1" }}>Beschreibung
              <textarea rows={3} value={val("beschreibung")} onChange={(e) => setEdit("beschreibung", e.target.value)} />
            </label>
          </div>
            );
          })()}

          {/* ── Dokumente ────────────────────────────────────────────── */}
          <div className="vs-section-header">
            <h4>Dokumente ({data.dokumente?.length || 0})</h4>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <select className="me-select me-select-sm" value={uploadRichtung}
                      onChange={(e) => setUploadRichtung(e.target.value)}>
                <option value="eingereicht">eingereicht (von uns)</option>
                <option value="eingehend">eingehend (an uns)</option>
              </select>
              <label className="me-btn me-btn-primary me-btn-sm" style={{ cursor: "pointer" }}>
                <FaPlus /> {uploadBusy ? "Lädt…" : "Datei hochladen"}
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.webp"
                       style={{ display: "none" }}
                       onChange={async (e) => {
                         const files = Array.from(e.target.files || []);
                         e.target.value = "";
                         if (!files.length) return;
                         setUploadBusy(true);
                         try {
                           for (const f of files) await schaedenApi.uploadDokument(schadenId, f, uploadRichtung);
                           await load(); onChanged?.();
                         } catch (err) { setError(err.message); }
                         setUploadBusy(false);
                       }} />
              </label>
              <button className="me-btn me-btn-secondary me-btn-sm" onClick={addDoc} title="Leere Zeile nur mit Angaben (ohne Datei)">
                <FaPlus /> Zeile
              </button>
            </div>
          </div>
          {data.dokumente?.length ? (
            <div className="me-table-wrap">
              <table className="me-table vs-doc-table">
                <thead>
                  <tr>
                    <th>Datei</th><th>Richtung</th><th>Typ</th><th>Firma</th><th>Beleg-Nr</th>
                    <th className="num">Betrag</th>
                    <th>Eingereicht</th><th>Rückmeldung</th>
                    <th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.dokumente.map((d) => (
                    <DokumentRow key={d.id} d={d} onPatch={updateDoc} onRemove={removeDoc} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: "#94a3b8", marginTop: 8 }}>Noch keine Dokumente erfasst.</p>
          )}
        </div>

        <div className="me-drawer-footer">
          <button className="me-btn me-btn-danger" onClick={removeSchaden}>
            <FaTrash /> Löschen
          </button>
          <div style={{ flex: 1 }} />
          <button className="me-btn me-btn-secondary" onClick={onClose}>Schließen</button>
          <button className="me-btn me-btn-primary" disabled={saving || !Object.keys(edits).length} onClick={save}>
            <FaSave /> {saving ? "Speichere…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Dokument-Zeile mit Inline-Edit ───────────────────────────────────── */
function DokumentRow({ d, onPatch, onRemove }) {
  const [local, setLocal] = useState({});
  const dirty = Object.keys(local).length > 0;
  const get = (k) => (local[k] !== undefined ? local[k] : d[k] ?? "");
  const flush = () => {
    if (dirty) {
      onPatch(d.id, local);
      setLocal({});
    }
  };
  return (
    <tr>
      <td style={{ maxWidth: 160 }}>
        {d.dropbox_pfad ? (
          <a href={schaedenApi.dokumentDownloadUrl(d.id)} target="_blank" rel="noreferrer"
             title={d.dropbox_pfad} style={{ color: "#a5b4fc", wordBreak: "break-all", fontSize: 12 }}>
            📎 {dateiName(d.dropbox_pfad)}
          </a>
        ) : <span style={{ color: "#64748b" }}>—</span>}
      </td>
      <td>
        <select value={get("richtung") || ""} onChange={(e) => setLocal((p) => ({ ...p, richtung: e.target.value || null }))} onBlur={flush}>
          <option value="">—</option>
          {RICHTUNG.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td><input value={get("typ")} onChange={(e) => setLocal((p) => ({ ...p, typ: e.target.value }))} onBlur={flush} /></td>
      <td><input value={get("firma")} onChange={(e) => setLocal((p) => ({ ...p, firma: e.target.value }))} onBlur={flush} /></td>
      <td><input value={get("beleg_nr")} onChange={(e) => setLocal((p) => ({ ...p, beleg_nr: e.target.value }))} onBlur={flush} /></td>
      <td className="num">
        <input type="number" step="0.01" value={get("betrag") || ""}
               onChange={(e) => setLocal((p) => ({ ...p, betrag: e.target.value ? Number(e.target.value) : null }))}
               onBlur={flush} />
      </td>
      <td><input type="date" value={get("eingereicht_am") ? String(get("eingereicht_am")).slice(0, 10) : ""}
                 onChange={(e) => setLocal((p) => ({ ...p, eingereicht_am: e.target.value || null }))} onBlur={flush} /></td>
      <td><input type="date" value={get("rueckmeldung_am") ? String(get("rueckmeldung_am")).slice(0, 10) : ""}
                 onChange={(e) => setLocal((p) => ({ ...p, rueckmeldung_am: e.target.value || null }))} onBlur={flush} /></td>
      <td>
        <select value={get("status") || ""} onChange={(e) => { setLocal((p) => ({ ...p, status: e.target.value || null })); }} onBlur={flush}>
          {STATUS_DOKUMENT.map((s) => <option key={s} value={s}>{s || "—"}</option>)}
        </select>
      </td>
      <td>
        <button className="me-btn me-btn-icon me-btn-danger" onClick={() => onRemove(d.id)}><FaTrash /></button>
      </td>
    </tr>
  );
}

/* ─── Neuer Schaden (Welle 89.1): Seiten-Drawer, erst Objekt → nur dessen Policen,
   Status/Schaden-Nr/Beschreibung gleich mit, Fotos/Dokumente direkt hochladen ── */
function NeuerSchadenDialog({ objekte, onClose, onCreated }) {
  const [objekt, setObjekt] = useState("");
  const [policen, setPolicen] = useState([]);
  const [alle, setAlle] = useState(false);
  const [versId, setVersId] = useState("");
  const [schadenstag, setSchadenstag] = useState("");
  const [art, setArt] = useState("");
  const [schadenNr, setSchadenNr] = useState("");
  const [status, setStatus] = useState("Angelegt");
  const [beschreibung, setBeschreibung] = useState("");
  const [dateien, setDateien] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState(null);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const params = !alle && objekt ? { objekt_kuerzel: objekt } : {};
        const v = await schaedenApi.listVersicherungen(params);
        if (aktiv) { setPolicen(v); setVersId((p) => (v.some((x) => String(x.id) === String(p)) ? p : "")); }
      } catch (e) { if (aktiv) setFehler(e.message); }
    })();
    return () => { aktiv = false; };
  }, [objekt, alle]);

  const anlegen = async () => {
    setBusy(true); setFehler(null);
    try {
      const r = await schaedenApi.create({
        objekt_kuerzel: objekt || null,
        versicherung_id: versId ? Number(versId) : null,
        schadenstag: schadenstag || null,
        art: art || null,
        schaden_nr: schadenNr || null,
        status: status || null,
        beschreibung: beschreibung || null,
      });
      for (const f of dateien) {
        await schaedenApi.uploadDokument(r.id, f, "eingereicht");
      }
      onCreated(r.id);
    } catch (e) { setFehler(e.message); }
    setBusy(false);
  };

  return (
    <div className="me-drawer-overlay" onClick={onClose}>
      <div className="me-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="me-drawer-header">
          <h3><FaShieldAlt /> Neuer Schaden</h3>
          <button className="me-btn me-btn-icon" onClick={onClose}><FaTimes /></button>
        </div>
        {fehler && <div className="me-status error">❌ {fehler}</div>}
        <div className="me-drawer-body">
        <div className="vs-form-grid">
          <label>Objekt — wo ist der Schaden?
            <select value={objekt} autoFocus onChange={(e) => { setObjekt(e.target.value); setAlle(false); }}>
              <option value="">– wählen –</option>
              {nurBasisObjekte(objekte).map((o) => (
                <option key={o.kuerzel} value={o.kuerzel}>{o.kuerzel}{o.bezeichnung ? ` – ${o.bezeichnung}` : ""}</option>
              ))}
            </select>
          </label>
          <label>Police {objekt && !alle ? `(nur ${objekt})` : "(alle)"}
            <select value={versId} onChange={(e) => setVersId(e.target.value)} disabled={!objekt && !alle}>
              <option value="">— ohne Police —</option>
              {policen.map((v) => (
                <option key={v.id} value={v.id}>{versOptionLabel(v)}</option>
              ))}
            </select>
            {objekt && !alle && (
              <button type="button" className="me-btn me-btn-sm me-btn-secondary" style={{ marginTop: 4 }}
                      onClick={() => setAlle(true)}>alle Policen anzeigen</button>
            )}
            {objekt && !alle && policen.length === 0 && (
              <span style={{ color: "#f97316", fontSize: 12 }}>Keine Police zu {objekt} hinterlegt.</span>
            )}
          </label>
          <label>Schadenstag
            <input type="date" value={schadenstag} onChange={(e) => setSchadenstag(e.target.value)} />
          </label>
          <label>Status
            <input list="vs-status-neu" value={status} onChange={(e) => setStatus(e.target.value)} />
            <datalist id="vs-status-neu">
              {STATUS_SCHADEN.map((st) => <option key={st} value={st} />)}
            </datalist>
          </label>
          <label>Art
            <input value={art} onChange={(e) => setArt(e.target.value)} placeholder="LW, Brand, Sturm, Glas…" />
          </label>
          <label>Schaden-Nr (optional — kommt oft erst später)
            <input value={schadenNr} onChange={(e) => setSchadenNr(e.target.value)} />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>Beschreibung
            <textarea rows={3} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>Dokumente / Fotos (werden beim Anlegen hochgeladen)
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.webp"
                   onChange={(e) => setDateien(Array.from(e.target.files || []))} />
            {dateien.length > 0 && (
              <span style={{ color: "#94a3b8", fontSize: 12 }}>
                {dateien.map((f) => f.name).join(", ")}
              </span>
            )}
          </label>
        </div>
        </div>
        <div className="me-drawer-footer">
          <div style={{ flex: 1 }} />
          <button className="me-btn me-btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="me-btn me-btn-primary" disabled={busy || !objekt} onClick={anlegen}>
            <FaPlus /> {busy ? "Lege an…" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Haupt-Tab ───────────────────────────────────────────────────────── */
export default function Tab4Schaeden() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [versicherungen, setVersicherungen] = useState([]);
  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  // Excel-artige Spaltenfilter: nur Werte, die wirklich vorkommen
  const [spalten, setSpalten] = useState({ jahr: "", objekt: "", vers: "", art: "", status: "" });

  const [drawerId, setDrawerId] = useState(null);
  const [neuOffen, setNeuOffen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, s, v, o] = await Promise.all([
        schaedenApi.list({}),
        schaedenApi.stats(),
        schaedenApi.listVersicherungen(),
        schaedenApi.listObjekte(),
      ]);
      setRows(r);
      setStats(s);
      setVersicherungen(v);
      setObjekte(o);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /* Welle 89: kein leerer Sofort-POST mehr — erst Objekt + Police im Dialog wählen. */
  const createNew = () => setNeuOffen(true);

  const filteredObjekte = useMemo(
    () => objekte.filter((o) => o.kuerzel).sort((a, b) => a.kuerzel.localeCompare(b.kuerzel)),
    [objekte]
  );

  const versLabel = (r) => (r.versicherung_id
    ? `${r.gesellschaft_kuerzel || r.gesellschaft || "?"} · ${r.versicherungsnummer || ""}`.trim()
    : "ohne Police");

  /* Excel-Prinzip: die Dropdowns zeigen nur Werte, die in den Daten vorkommen. */
  const distinct = useMemo(() => {
    const j = new Set(), o = new Set(), v = new Set(), a = new Set(), st = new Set();
    rows.forEach((r) => {
      if (r.schadenstag) j.add(String(r.schadenstag).slice(0, 4));
      if (r.objekt_kuerzel) o.add(r.objekt_kuerzel);
      v.add(versLabel(r));
      if (r.art) a.add(r.art);
      if (r.status) st.add(r.status);
    });
    const sortiert = (x) => [...x].sort((p, q2) => String(p).localeCompare(String(q2)));
    return { jahre: sortiert(j).reverse(), objekte: sortiert(o), vers: sortiert(v), arten: sortiert(a), status: sortiert(st) };
  }, [rows]);

  const sichtbar = useMemo(() => rows.filter((r) => {
    if (spalten.jahr && String(r.schadenstag || "").slice(0, 4) !== spalten.jahr) return false;
    if (spalten.objekt && r.objekt_kuerzel !== spalten.objekt) return false;
    if (spalten.vers && versLabel(r) !== spalten.vers) return false;
    if (spalten.art && r.art !== spalten.art) return false;
    if (spalten.status && r.status !== spalten.status) return false;
    if (q.trim()) {
      const t = `${r.schaden_nr || ""} ${r.polizeiliches_az || ""} ${r.beschreibung || ""} ${r.art || ""} ${versLabel(r)}`.toLowerCase();
      if (!t.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  }), [rows, spalten, q]);

  return (
    <>
      {/* ── KPI-Karten ────────────────────────────────────────────── */}
      <div className="me-kpis me-kpis-small">
        <div className="me-kpi-card">
          <span className="me-kpi-label">Gesamt</span>
          <span className="me-kpi-value blue">{stats?.total ?? 0}</span>
        </div>
        <div className="me-kpi-card kpi-offen">
          <span className="me-kpi-label">Offen</span>
          <span className="me-kpi-value red">
            {(stats?.by_status?.["Offen"] || 0) + (stats?.by_status?.[""] || 0)}
          </span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Erledigt / Geschlossen</span>
          <span className="me-kpi-value green">
            {Object.entries(stats?.by_status || {})
              .filter(([k]) => /erledigt|abgeschlossen|geschlossen|bezahlt/i.test(k))
              .reduce((a, [, c]) => a + c, 0)}
          </span>
        </div>
        <div className="me-kpi-card kpi-offen">
          <span className="me-kpi-label">Nachhaken (>30 T.)</span>
          <span className="me-kpi-value red">{stats?.nachhaken ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Ohne Police</span>
          <span className="me-kpi-value">{stats?.ohne_police ?? 0}</span>
        </div>
      </div>

      {error && <div className="me-status error">❌ {error}</div>}

      {/* ── Filter + Aktionen ─────────────────────────────────────── */}
      <div className="me-table-section">
        <div className="me-table-header">
          <h3><FaShieldAlt style={{ marginRight: 6 }} /> Schadensfälle ({sichtbar.length}{sichtbar.length !== rows.length ? ` von ${rows.length}` : ""})</h3>
          <div className="me-filters">
            <div className="me-search-wrap">
              <FaSearch className="me-search-icon" />
              <input className="me-filter-input" placeholder="Suche…"
                     value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <button className="me-btn me-btn-icon" title="Aktualisieren" onClick={reload}>
              <FaSync className={loading ? "spin" : ""} />
            </button>
            <button className="me-btn me-btn-primary me-btn-sm" onClick={createNew}>
              <FaPlus /> Neuer Schaden
            </button>
          </div>
        </div>

        {/* ── Tabelle ──────────────────────────────────────────────── */}
        <div className="me-table-wrap">
          <table className="me-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Objekt</th>
                <th>Versicherung</th>
                <th>Art</th>
                <th>Schaden-Nr</th>
                <th>Status</th>
                <th className="num">Höhe</th>
                <th>Dokumente</th>
              </tr>
              {/* Excel-artige Spaltenfilter — nur vorkommende Werte */}
              <tr className="vs-spaltenfilter">
                {[["jahr", distinct.jahre, "Jahr"], ["objekt", distinct.objekte, "Objekt"],
                  ["vers", distinct.vers, "Versicherung"], ["art", distinct.arten, "Art"]].map(([k, werte, ph]) => (
                  k === "art" ? (
                    <React.Fragment key={k}>
                      <th><FilterSelect wert={spalten[k]} werte={werte} ph={ph}
                                        onWert={(v) => setSpalten((p) => ({ ...p, [k]: v }))} /></th>
                      <th></th>
                    </React.Fragment>
                  ) : (
                    <th key={k}><FilterSelect wert={spalten[k]} werte={werte} ph={ph}
                                              onWert={(v) => setSpalten((p) => ({ ...p, [k]: v }))} /></th>
                  )
                ))}
                <th><FilterSelect wert={spalten.status} werte={distinct.status} ph="Status"
                                  onWert={(v) => setSpalten((p) => ({ ...p, status: v }))} /></th>
                <th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {sichtbar.length === 0 && !loading && (
                <tr><td colSpan={8} style={{ textAlign: "center", color: "#94a3b8", padding: 20 }}>
                  Keine Treffer.
                </td></tr>
              )}
              {sichtbar.map((r) => (
                <tr key={r.id} onClick={() => setDrawerId(r.id)} style={{ cursor: "pointer" }}>
                  <td>
                    {r.schadenstag
                      ? (r.jahr_geschaetzt ? fmtJahr(r.schadenstag) : fmtDate(r.schadenstag))
                      : <span style={{ color: "#94a3b8" }}>—</span>}
                  </td>
                  <td>{r.objekt_kuerzel || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td>
                    {r.versicherung_id ? (
                      <span>
                        {r.gesellschaft_kuerzel || r.gesellschaft || "?"}
                        <span style={{ color: "#94a3b8", marginLeft: 4 }}>· {r.versicherungsnummer}</span>
                      </span>
                    ) : (
                      <span style={{ color: "#f97316" }}>
                        <FaExclamationTriangle /> ohne Police
                      </span>
                    )}
                  </td>
                  <td>{r.art}</td>
                  <td>{r.schaden_nr}</td>
                  <td>
                    <span className={`me-badge ${statusClass(r.status)}`}>
                      {r.status || "—"}
                    </span>
                  </td>
                  <td className="num">{fmtEUR(r.hoehe_real || r.hoehe_geschaetzt)}</td>
                  <td>
                    <span style={{ color: "#94a3b8" }}>
                      {r.dokumente_count}× · {r.eingereicht_count} eingereicht
                      {r.zu_erstellen_count > 0 && (
                        <span style={{ color: "#f97316", marginLeft: 6 }}>
                          · {r.zu_erstellen_count} zu erstellen
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {neuOffen && (
        <NeuerSchadenDialog
          objekte={filteredObjekte}
          onClose={() => setNeuOffen(false)}
          onCreated={async (id) => { setNeuOffen(false); setDrawerId(id); await reload(); }}
        />
      )}

      {drawerId !== null && (
        <SchadenDrawer
          schadenId={drawerId}
          versicherungen={versicherungen}
          objekte={filteredObjekte}
          onClose={() => setDrawerId(null)}
          onChanged={reload}
        />
      )}
    </>
  );
}
